export const extractYouTubeId = (urlStr) => {
  try {
    let cleanUrl = urlStr.trim();
    if (!cleanUrl.startsWith("http://") && !cleanUrl.startsWith("https://")) {
      cleanUrl = `https://${cleanUrl}`;
    }
    const urlObj = new URL(cleanUrl);
    if (urlObj.hostname.includes("youtube.com") || urlObj.hostname.includes("youtu.be")) {
      let videoId = "";
      if (urlObj.searchParams.get("v")) {
        videoId = urlObj.searchParams.get("v");
      } else if (urlObj.hostname.includes("youtu.be")) {
        videoId = urlObj.pathname.slice(1);
      } else if (urlObj.pathname.includes("/shorts/")) {
        videoId = urlObj.pathname.split("/shorts/")[1];
      } else if (urlObj.pathname.includes("/embed/")) {
        videoId = urlObj.pathname.split("/embed/")[1];
      } else if (urlObj.pathname.includes("/v/")) {
        videoId = urlObj.pathname.split("/v/")[1];
      }

      if (videoId) {
        return videoId.split("&")[0].split("?")[0].split("/")[0];
      }
    }
  } catch (e) {
    return null;
  }
  return null;
};

export const fixIframeUrls = (html) => {
  if (!html) return html;

  let processedHtml = html.trim();

  // If the input is just a plain YouTube URL without HTML tags
  if (!processedHtml.includes("<") && (processedHtml.includes("youtube.com") || processedHtml.includes("youtu.be"))) {
    const videoId = extractYouTubeId(processedHtml);
    if (videoId) {
      return `<iframe src="https://www.youtube.com/embed/${videoId}" width="100%" height="100%" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>`;
    }
  }

  // Replace iframe src attributes
  return processedHtml.replace(/<iframe([^>]*)\s*src=["']([^"']+)["']([^>]*)>/gi, (match, beforeSrc, src, afterSrc) => {
    let fixedSrc = src.trim();

    if (fixedSrc.startsWith("//")) {
      fixedSrc = `https:${fixedSrc}`;
    } else if (!fixedSrc.startsWith("http://") && !fixedSrc.startsWith("https://")) {
      fixedSrc = `https://${fixedSrc}`;
    }

    try {
      const videoId = extractYouTubeId(fixedSrc);
      if (videoId) {
        fixedSrc = `https://www.youtube.com/embed/${videoId}`;
      }
    } catch (e) {
      // If URL parsing fails, keep fixedSrc as is
    }

    let fullIframe = `<iframe${beforeSrc} src="${fixedSrc}"${afterSrc}>`;

    if (fixedSrc.includes("youtube.com/embed/")) {
      if (!fullIframe.includes("allow=")) {
        fullIframe = fullIframe.replace("<iframe", '<iframe allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"');
      }
      if (!fullIframe.includes("allowfullscreen")) {
        fullIframe = fullIframe.replace("<iframe", "<iframe allowfullscreen");
      }
    }

    return fullIframe;
  });
};

export const fixInvalidLinks = (html) => {
  return html.replace(/<a([^>]*href=["']([^"']+)["'][^>]*)>/gi, (match, attributes, href) => {
    const cleanHref = href.trim();

    const isInvalidLink =
      !cleanHref ||
      cleanHref === "" ||
      cleanHref === "#" ||
      // eslint-disable-next-line no-script-url
      cleanHref === "javascript:void(0)" ||
      // eslint-disable-next-line no-script-url
      cleanHref === "javascript:;" ||
      // eslint-disable-next-line no-script-url
      (cleanHref.startsWith("javascript:") && cleanHref.length <= 12);

    const isValidAnchorLink = cleanHref && cleanHref.startsWith("#") && cleanHref.length > 1;

    if (cleanHref.startsWith("mailto:") || cleanHref.startsWith("tel:") || cleanHref.startsWith("blob:") || cleanHref.startsWith("data:") || /\bdownload\b/i.test(attributes)) {
      return match;
    }

    if (isInvalidLink) {
      const cleanAttributes = attributes.replace(/href=["'][^"']*["']/gi, "");
      return `<a${cleanAttributes} style="text-decoration: none; color: inherit;">`;
    }

    if (isValidAnchorLink) {
      const cleanAttributes = attributes.replace(/target=["'][^"']*["']/gi, "");
      return `<a${cleanAttributes}>`;
    }

    // Fix protocol if missing for web links
    let fixedHref = cleanHref;
    if (!fixedHref.startsWith("http://") && !fixedHref.startsWith("https://")) {
      fixedHref = `https://${fixedHref}`;
    }

    // If YouTube link, format to direct watch URL for opening directly on YouTube
    if (fixedHref.includes("youtube.com") || fixedHref.includes("youtu.be")) {
      const videoId = extractYouTubeId(fixedHref);
      if (videoId) {
        fixedHref = `https://www.youtube.com/watch?v=${videoId}`;
      }
    }

    // Replace href in attributes if changed, and force target="_blank"
    let cleanAttributes = attributes.replace(/href=["'][^"']*["']/gi, `href="${fixedHref}"`).replace(/target=["'][^"']*["']/gi, "");

    return `<a${cleanAttributes} target="_blank" rel="noopener noreferrer">`;
  });
};

export const addSecurityTags = (html) => {
  const securityTags = `
      <base target="_self">
    `;

  if (html.includes("<head>")) {
    return html.replace("<head>", () => `<head>${securityTags}`);
  } else if (html.includes("<html>")) {
    return html.replace("<html>", () => `<html><head>${securityTags}</head>`);
  } else {
    return `<head>${securityTags}</head>${html}`;
  }
};

export const addClickPrevention = (html) => {
  const preventionScript = `
      <script>
        document.addEventListener('DOMContentLoaded', function() {
          // Handle anchor links for smooth scrolling within the iframe
          var anchorLinks = document.querySelectorAll('a[href^="#"]');
          anchorLinks.forEach(function(link) {
            var href = link.getAttribute('href');
            
            // For valid anchor links with fragments, enable smooth scrolling
            if (href && href.length > 1 && href !== '#') {
              link.addEventListener('click', function(e) {
                e.preventDefault();
                var targetId = href.substring(1);
                var targetElement = document.getElementById(targetId);
                
                if (targetElement) {
                  targetElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              });
            }
          });
          
          // Handle other invalid links that are pure placeholders (without click handlers or download attributes)
          var allLinks = document.querySelectorAll('a');
          allLinks.forEach(function(link) {
            var href = link.getAttribute('href');
            if ((href === 'javascript:void(0)' || href === 'javascript:;' || href === '#') && !link.hasAttribute('download') && !link.hasAttribute('onclick')) {
              link.addEventListener('click', function(e) {
                if (!link.onclick && !link.hasAttribute('download')) {
                  e.preventDefault();
                }
              });
            }
          });
          
          // Prevent form submissions that might cause navigation issues (unless action is set)
          var forms = document.querySelectorAll('form');
          forms.forEach(function(form) {
            if (form.getAttribute('action') === '#') {
              form.addEventListener('submit', function(e) {
                e.preventDefault();
                e.stopPropagation();
                return false;
              });
            }
          });
        });
      </script>
    `;

  if (html.includes("</body>")) {
    return html.replace("</body>", () => `${preventionScript}</body>`);
  } else {
    return `${html}${preventionScript}`;
  }
};

export const prepareEmbeddedHtml = (rawHtml) => {
  const nativeHtmlReset = `
    <style>
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        color: #1a1a1a;
        line-height: 1.5;
        padding: 0;
        margin:0
      }
      h1, h2, h3, h4, h5, h6 { font-weight: bold; display: block; margin-top: 0.67em; margin-bottom: 0.67em; }
      h1 { font-size: 2em; }
      h2 { font-size: 1.5em; }
      h3 { font-size: 1.17em; }
      h4 { font-size: 1em; }
      h5 { font-size: 0.83em; }
      h6 { font-size: 0.67em; }
      p { display: block; margin-top: 1em; margin-bottom: 1em; }
      ul { display: block; list-style-type: disc; padding-left: 40px; margin-top: 1em; margin-bottom: 1em; }
      ol { display: block; list-style-type: decimal; padding-left: 40px; margin-top: 1em; margin-bottom: 1em; }
      li { display: list-item; }
      strong, b { font-weight: bold; }
      em, i { font-style: italic; }
      a { text-decoration: none; }
      blockquote { display: block; margin: 1em 40px; padding-left: 15px; border-left: 4px solid #e5e7eb; color: #4b5563; }
      code { font-family: monospace; background-color: #f3f4f6; padding: 0.2em 0.4em; rounded: 4px; }
      pre { display: block; font-family: monospace; white-space: pre; margin: 1em 0; background-color: #f3f4f6; padding: 1rem; overflow-x: auto; }
    </style>
  `;

  const cssLinks = `
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css">
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.css">
  `;

  if (rawHtml.includes("<html") || rawHtml.includes("<body")) {
    let structured = rawHtml;
    if (structured.includes("</head>")) {
      return structured.replace("</head>", () => `${cssLinks}${nativeHtmlReset}</head>`);
    }
    return `${cssLinks}${nativeHtmlReset}${structured}`;
  }

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      ${cssLinks}
      ${nativeHtmlReset}
    </head>
    <body>
      ${rawHtml}
    </body>
    </html>
  `.trim();
};
